import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import FilterChips from 'components/shared/FilterChips';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import { useProfile } from 'context/profile';
import useIsMobile from 'hooks/useIsMobile';
import { DischargeCalendarFilterValue } from 'models/userPreferences/DischargeCalendarFilter';
import { dischargeCalendarQueryKeys } from 'services/api/dischargeCalendar';
import { upsertDischargeCalendarFilter } from 'services/api/preferences';
import {
  useDischargeCalendarCurrentView,
  useDischargeCalendarFilters,
  useDischargeCalendarFiltersActions,
  useDischargeCalendarStore,
} from 'stores/dischargeCalendarStore';

import { DISCHARGE_CALENDAR_ROUTES, DISCHARGE_CALENDAR_VIEWS } from './constants';
import DischargeCalendarFilterBar from './DischargeCalendarFilterBar';

export default function DischargeCalendarPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  const currentView = useDischargeCalendarCurrentView();
  const filters = useDischargeCalendarFilters();
  const { removeFilter, setCurrentView, setSearch } = useDischargeCalendarFiltersActions();

  const { mutate: savePreferences } = useMutation({
    mutationFn: upsertDischargeCalendarFilter,
  });

  // Invalidate query cache and clear search when leaving the page
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: [dischargeCalendarQueryKeys.base] });
      setSearch('');
    };
  }, [queryClient, setSearch]);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const viewFromUrl = pathParts[pathParts.length - 1] as keyof typeof DISCHARGE_CALENDAR_VIEWS;

    if (Object.values(DISCHARGE_CALENDAR_VIEWS).includes(viewFromUrl as any)) {
      setCurrentView(viewFromUrl as any);
    }
  }, [location.pathname, setCurrentView]);

  useEffect(() => {
    if (location.pathname === DISCHARGE_CALENDAR_ROUTES.BASE) {
      const defaultView = isMobile ? DISCHARGE_CALENDAR_VIEWS.DAY : currentView || DISCHARGE_CALENDAR_VIEWS.WEEK;
      const defaultRoute =
        DISCHARGE_CALENDAR_ROUTES[defaultView.toUpperCase() as keyof typeof DISCHARGE_CALENDAR_ROUTES];
      navigate(defaultRoute, { replace: true });
    }
  }, [location.pathname, navigate, isMobile, currentView]);

  useEffect(() => {
    const mobileRestrictedRoutes: string[] = [DISCHARGE_CALENDAR_ROUTES.WEEK, DISCHARGE_CALENDAR_ROUTES.MONTH];
    if (isMobile && mobileRestrictedRoutes.includes(location.pathname)) {
      navigate(DISCHARGE_CALENDAR_ROUTES.DAY, { replace: true });
    }
  }, [isMobile, location.pathname, navigate]);

  const handleRemoveFilter = (filterKey: string, id: string) => {
    removeFilter(filterKey, id);
    const updatedFilters = {
      ...filters,
      ...(filters[filterKey] ? { [filterKey]: filters[filterKey].removeFilter(id) } : {}),
    };
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...updatedFilters, currentView } as DischargeCalendarFilterValue,
    });
  };

  return (
    <Page>
      <DischargeCalendarFilterBar onApplyFilters={() => {}} />
      <Page.Content>
        <ContentWrapper>
          <DesktopOnly>
            <FilterChips filters={filters} onRemoveFilter={handleRemoveFilter} useStore={useDischargeCalendarStore} />
          </DesktopOnly>
          <CalendarContent>
            <Outlet />
          </CalendarContent>
        </ContentWrapper>
      </Page.Content>
    </Page>
  );
}

const ContentWrapper = styled.div`
  padding: 0px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CalendarContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
